import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { AuditStats } from './AuditStats';
import { AuditLogsTable } from './AuditLogsTable';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { UserTypeFilter } from '@/hooks/usePaginatedMediaData';

const eventTypes = [
  { value: 'all', label: 'Todos os Eventos' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'location_update', label: 'Atualização de Localização' },
  { value: 'location_skip', label: 'Pular Localização' },
  { value: 'profile_update', label: 'Atualização de Perfil' },
  { value: 'password_change', label: 'Mudança de Senha' },
  { value: 'email_change', label: 'Mudança de Email' },
  { value: 'match_created', label: 'Match Criado' },
  { value: 'message_sent', label: 'Mensagem Enviada' },
  { value: 'connection_created', label: 'Conexão Criada' },
  { value: 'connection_deleted', label: 'Conexão Deletada' },
  { value: 'plan_change', label: 'Mudança de Plano' },
  { value: 'photo_upload', label: 'Upload de Foto' },
  { value: 'photo_delete', label: 'Foto Deletada' },
  { value: 'verification_submitted', label: 'Verificação Enviada' },
  { value: 'verification_approved', label: 'Verificação Aprovada' },
  { value: 'verification_rejected', label: 'Verificação Rejeitada' },
];

interface AdminAuditProps {
  userTypeFilter?: UserTypeFilter;
}

export const AdminAudit: React.FC<AdminAuditProps> = ({ userTypeFilter = 'all' }) => {
  const [eventType, setEventType] = useState('all');
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  const pageSize = 50;

  const { data, isLoading } = useAuditLogs({
    eventType: eventType === 'all' ? undefined : eventType,
    startDate: dateRange.startDate ? new Date(dateRange.startDate) : undefined,
    endDate: dateRange.endDate ? new Date(dateRange.endDate) : undefined,
    page,
    pageSize,
    userTypeFilter,
  });

  return (
    <div className="px-4 space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-white">Auditoria</h2>
        <p className="text-white/60">
          Monitore e analise todos os eventos de auditoria do sistema
        </p>
      </div>

      <AuditStats userTypeFilter={userTypeFilter} />

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Filtros</CardTitle>
          <CardDescription className="text-white/60">
            Filtre os logs de auditoria por tipo de evento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Selecione o tipo de evento" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input
                type="date"
                placeholder="Data inicial"
                value={dateRange.startDate || ''}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, startDate: e.target.value || undefined }));
                  setPage(1);
                }}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                type="date"
                placeholder="Data final"
                value={dateRange.endDate || ''}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, endDate: e.target.value || undefined }));
                  setPage(1);
                }}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <AuditLogsTable
        logs={data?.logs || []}
        isLoading={isLoading}
        page={page}
        totalCount={data?.count || 0}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </div>
  );
};
